import * as Api from '@ViewModels';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';

const BASE_QUERY_KEY = 'actionItem/filter';

/** Fetch a generous page so the scan usually needs a single request. */
const UNDATED_PAGE_SIZE = 100;

/**
 * Hard cap on the scan. The `actionItem/filter` API has no "due date is empty" selector
 * (FilterOperator is only Gt/Lt/…), so the only way to surface undated items is to fetch items
 * *without* a due-date criterion and filter them client-side. That query returns every action
 * item, so we bound it: scan at most this many pages, then stop. Anything past the cap is reported
 * via {@link IUseCalendarUndatedActionItemsResult.hasMore} so the UI can show an honest "N+"
 * roll-up rather than silently implying it found them all.
 */
const MAX_SCAN_PAGES = 3;

export interface IUseCalendarUndatedActionItemsResult {
	/** Undated (no due date), in-progress action items found within the scanned pages. */
	undatedActionItems: Api.IActionItem[];
	/** True while the first page is loading. */
	isLoading: boolean;
	/**
	 * True when the scan hit its page cap with more pages still available, so there may be more
	 * undated items than were found. Drives the "N+" (rather than exact "N") roll-up label.
	 */
	hasMore: boolean;
}

/**
 * Fetch in-progress action items that have **no due date**, to place on today's all-day band.
 *
 * There is no server-side "no due date" filter, so this sends the action-item criteria *without*
 * a `DueDate` range (which would exclude undated items) and filters to `!dueDate` client-side,
 * scanning a bounded number of pages (most-recently-modified first, so freshly-created undated
 * work surfaces). Scope mirrors {@link useCalendarActionItems}: `assignedToUserId` restricts to
 * one assignee; `undefined` returns everyone's. Callers should only enable this when today is in
 * the visible window (undated items are placed on today).
 */
export function useCalendarUndatedActionItems({
	assignedToUserId,
	search = '',
	impersonationContext,
	enabled = true,
}: {
	/** Restrict to one assignee; omit/undefined to include all users' action items. */
	assignedToUserId?: string;
	/** Free-text search over action items (matches the list's search). */
	search?: string;
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
}): IUseCalendarUndatedActionItemsResult {
	const userSession = useUserSession();

	// Note: no DueDate criterion — that would exclude the very items we want. We rely on the
	// client-side `!dueDate` filter below to keep only the undated ones.
	const filterRequest = React.useMemo<Api.IRichContentRequest>(() => {
		const criteria: Api.IRichContentRequest['criteria'] = [
			{ property: Api.RichContentProperty.IsCompleted, value: 'false' },
			{ property: Api.RichContentProperty.Type, value: 'ActionItem' },
			// A specific assignee id scopes to that user; `undefined` leaves it unrestricted (all users).
			{ property: Api.RichContentProperty.AssignedTo, value: assignedToUserId },
		];
		// The filter endpoint does free-text search via a `Content` criterion, not a query param —
		// this mirrors how the Action Items list's VM applies its search.
		const trimmedSearch = search.trim();
		if (trimmedSearch) {
			criteria.push({ property: Api.RichContentProperty.Content, value: trimmedSearch });
		}
		return { criteria };
	}, [assignedToUserId, search]);

	const query = useInfiniteQuery<Api.IPagedCollection<Api.IActionItem>>({
		enabled,
		placeholderData: keepPreviousData,
		getNextPageParam: lastPage => lastPage.pageToken,
		queryFn: async ({ pageParam }) => {
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IActionItem>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: {
						currentUserOnly: false,
						pageSize: UNDATED_PAGE_SIZE,
						pageToken: pageParam,
						sortBy: 'lastModifiedDate',
						sort: 'desc',
					},
					urlPath: BASE_QUERY_KEY,
				}),
				'POST',
				filterRequest
			);
		},
		queryKey: [BASE_QUERY_KEY, 'undated', filterRequest, assignedToUserId, search, impersonationContext?.account?.id],
		initialPageParam: '',
		meta: { toastErrorMessage: 'Failed to load action items' },
	});

	const { hasNextPage, isFetchingNextPage, fetchNextPage, isFetchNextPageError } = query;
	const pagesLoaded = query.data?.pages.length ?? 0;

	// Past the cap we stop and report `hasMore` rather than chasing an unbounded fetch. See
	// `useCalendarMeetings` for why the error guard is required to bound this scan.
	React.useEffect(() => {
		if (enabled && hasNextPage && !isFetchingNextPage && !isFetchNextPageError && pagesLoaded < MAX_SCAN_PAGES) {
			fetchNextPage();
		}
	}, [enabled, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage, pagesLoaded]);

	const undatedActionItems = React.useMemo(
		() => (query.data?.pages.flatMap(page => page.values) ?? []).filter(actionItem => !actionItem.dueDate),
		[query.data?.pages]
	);

	return {
		undatedActionItems,
		isLoading: enabled ? query.isPending : false,
		hasMore: pagesLoaded >= MAX_SCAN_PAGES && !!hasNextPage,
	};
}
